import { NgClass } from "@angular/common";
import { Component, computed, inject, input, ChangeDetectionStrategy } from "@angular/core";

import { NavigationEnd, Router } from "@angular/router";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { AdditionalMenuItem, SideBarMenuItem } from "../../../models/ui/sideBar";
import { SvgIconComponent } from "../../components/svg-icon/svg-icon.component";
import { AuthService } from "../../../services/api/auth/auth.service";

import { SideBarBuilderService } from "./facade/side-bar-builder/side-bar-builder.service";
import { HasPermissionDirective } from "../../directives/has-permission/has-permission.directive";
import { PermissionService } from "../../../services/api/permission/permission.service";
import { SimplebarAngularModule } from "simplebar-angular";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { DialogService, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { PopoverModule } from "@openng/optimus-ui/popover";
import { ProfileSettingsComponent } from "../../components/profile-settings/profile-settings.component";
import { ProfileSettingsDialogHeaderComponent } from "../../components/profile-settings/profile-settings-dialog-header.component";

@Component({
  standalone: true,
  selector: "app-side-bar",
  imports: [NgClass, TranslateModule, SvgIconComponent, HasPermissionDirective, SimplebarAngularModule, PopoverModule],
  templateUrl: "./side-bar.component.html",
  styleUrl: "./side-bar.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [SideBarBuilderService],
})
export class SideBarComponent {
  public notificationCount = input<number>(0);
  public menuItems!: SideBarMenuItem[];
  public readonly versionNumber = "2.5.0";

  public permissions = inject(PermissionService);

  private auth = inject(AuthService);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private sideBarBuilder = inject(SideBarBuilderService);
  private dialogService = inject(DialogService);
  private profileSettingsDialog: DynamicDialogRef | null = null;

  public selectedIndex: number | null = null;
  public selectedIndexChildren: number | null = null;
  protected readonly authenticatedUser = toSignal(this.auth.getAuthenticatedUser(), { initialValue: null });
  protected readonly userDisplayName = computed(() => {
    const user = this.authenticatedUser();

    return [user?.firstName, user?.lastName]
      .map((name) => name?.trim())
      .filter((name): name is string => Boolean(name))
      .join(" ");
  });

  constructor() {
    this.menuItems = this.sideBarBuilder.buildSideBar();

    // Sets global variables of selected index to display selected menu item in UI:
    this.router.events.pipe(takeUntilDestroyed()).subscribe({
      next: (event) => {
        // Only start logic if event is NavigationEnd type
        if (event instanceof NavigationEnd) {
          this.selectedIndex = null;
          this.selectedIndexChildren = null;
          if (event.url) {
            this.checkUrlMatch(event.url);
          }
        }
      },
    });
  }

  /**
   * Compares the given URL with the path of the menu items to determine the
   * currently selected item in the side bar.
   * @param url The URL to compare with the menu items
   */
  private checkUrlMatch(url: string) {
    // urlSections at index 1 is top level path name and remove content after "?" in case of query Params
    const urlSections = url.split("/").map((section) => {
      const queryParamsSeparatorIndex = section.indexOf("?");
      if (queryParamsSeparatorIndex !== -1) {
        return section.slice(0, queryParamsSeparatorIndex);
      } else {
        return section;
      }
    });

    const index = this.menuItems.findIndex((menuItem) => {
      // find index where the path matches
      return menuItem.path === urlSections[2];
    });
    // if index was found on first level:
    if (index > -1) {
      this.selectedIndex = index;
      // if not, search in the children arrays:
    } else {
      this.menuItems.forEach((item, parentIndex) => {
        if (item.children && item.children.length > 0) {
          const foundIndexChildren = item.children.findIndex((child) => {
            return child.path === urlSections[2];
          });
          // if path was found save both index of parent and child
          if (foundIndexChildren > -1) {
            this.selectedIndex = parentIndex;
            this.selectedIndexChildren = foundIndexChildren;
            // open parent of selected menu item
            this.menuItems[parentIndex].showChildren = true;
          }
        }
      });
    }
  }

  /**
   * Handles the click event on a menu item.
   * If the clicked item has children, toggles the submenu.
   * If the clicked item has a path, navigates to the specified path.
   *
   * @param index - The index of the menu item that was clicked.
   */
  public menuItemClicked(index: number) {
    const menuItem = this.menuItems[index];
    if (menuItem.children && menuItem.children.length > 0) {
      this.toggleSubMenu(index);
    } else if (menuItem.path) {
      this.navigateTo(menuItem.path);
    }
  }

  /**
   * Navigates to a specified path.
   * Prepends 'dashboard/' to the path.
   * @param path The path to navigate to.
   */
  public navigateTo(path: string) {
    this.router.navigate(["dashboard/" + path]);
  }

  /**
   * Toggles the visibility of the submenu for the menu item at the given index.
   * @param index - The index of the menu item to toggle the submenu for.
   */
  private toggleSubMenu(index: number) {
    this.menuItems[index].showChildren = !this.menuItems[index].showChildren;
  }

  public openProfileSettings(): void {
    if (this.profileSettingsDialog) {
      return;
    }

    const dialogRef = this.dialogService.open(ProfileSettingsComponent, {
      templates: { header: ProfileSettingsDialogHeaderComponent },
      modal: true,
      width: "50vw",
      contentStyle: { overflow: "auto", padding: "20px" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      closable: true,
      dismissableMask: false,
    });
    if (!dialogRef) {
      return;
    }

    this.profileSettingsDialog = dialogRef;
    dialogRef.onClose.subscribe(() => {
      if (this.profileSettingsDialog === dialogRef) {
        this.profileSettingsDialog = null;
      }
    });
  }

  /**
   * Returns the link for a given path based on the current language.
   * The supported paths are:
   * - "imprint"
   * - "data_privacy"
   * - "user_docs"
   * If the path is not supported, an empty string is returned.
   * @param path - The path to get the link for.
   * @returns The link for the given path or an empty string.
   */
  public getLink(path: string) {
    const lang = this.translate.currentLang === "en" ? "en" : "de";
    const linkMap: {
      [lang: string]: AdditionalMenuItem;
      de: AdditionalMenuItem;
      en: AdditionalMenuItem;
    } = {
      de: {
        imprint: "https://www.botevo.com/impressum",
        data_privacy: "https://botevo.com/datenschutz",
        user_docs:
          "https://cdn.prod.website-files.com/67370d828a7072c40b7c10cb/67d0256f7e0f85d807da7df6_Handbuch%20BOTEVO%20-%20de.pdf",
      },
      en: {
        imprint: "https://www.botevo.com/en/impressum",
        data_privacy: "https://botevo.com/en/datenschutz",
        user_docs:
          "https://cdn.prod.website-files.com/67370d828a7072c40b7c10cb/67d0256f7e0f85d807da7df6_Handbuch%20BOTEVO%20-%20de.pdf",
      },
    };
    return linkMap[lang][path] ?? "";
  }

  /**
   * Logs out the user by calling the logout method of the AuthService.
   * Then, navigates to the login page.
   */
  public logout() {
    this.auth.logout();
    this.router.navigate(["/login"]);
  }
}
